import React from 'react'
import { useTranslation } from 'react-i18next'
import messageUtil from 'utils/messageUtil'
import HeatmapChart from 'components/visualization/Heatmap'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { CrosstabWidgetsType, CrosstabData } from '../../types'
import { isCellDisabled } from '../utils/displayDataUtils'
import { getWidgetSettingConfig } from '../utils/crosstabDataUtils'
import {
  getCrossTabSetting,
  getValueFormatter,
  getColorSetting,
  getRange,
  getDisabledCellToolTip
} from './utils'

const HeatmapDisplay = ({
  widgetId,
  crosstabData,
  onClick,
  enableTooltip
}: {
  widgetId: number
  crosstabData: CrosstabData
  onClick: (target: any) => void
  enableTooltip?: boolean
}) => {
  const widgetSetting = useDashboardWidgetSetting<CrosstabWidgetsType>(widgetId)
  const { t } = useTranslation()

  const { displayValue, displayShare, displayRowGrandTotal } = getWidgetSettingConfig(widgetSetting)
  const {
    crossTabs,
    rowIds,
    rowNames,
    columnIds,
    columnNames,
    rowComparisonMode,
    colComparisonMode
  } = crosstabData
  const {
    heatmapSeriesData,
    heatmapRowIds,
    heatmapRowNames,
    heatmapColIds,
    heatmapColNames
  } = getCrossTabSetting(
    crossTabs,
    widgetSetting,
    rowIds.map((rowId) => rowId.toString()),
    rowNames,
    columnIds.map((columnId) => columnId.toString()),
    columnNames,
    rowComparisonMode!,
    colComparisonMode!
  )
  const [rangeMin, rangeMax] = getRange(displayShare, widgetSetting.viewOption!)
  const formattedData = heatmapSeriesData.map((data) => ({
    x: data.x,
    y: data.y,
    value: data.value,
    isDisabled: isCellDisabled(data),
    tooltipContent: getDisabledCellToolTip(data),
    isAggregation: data.total || data.isRowGrandTotal,
    custom: {
      colId: heatmapColIds[data.x],
      rowId:
        heatmapRowIds.length === heatmapRowNames.length
          ? heatmapRowIds[data.y]
          : heatmapRowIds[data.y - 1]
    }
  }))

  const valueFormatter = getValueFormatter(
    widgetSetting.viewOption!,
    displayValue,
    displayRowGrandTotal
  )
  return (
    <HeatmapChart
      xCategories={heatmapColNames}
      yCategories={heatmapRowNames}
      data={formattedData}
      valueFormatter={valueFormatter}
      xAxisTooltipLabel={
        messageUtil.comparisonModeOptionSingular(colComparisonMode!) || t('metric')
      }
      yAxisTooltipLabel={messageUtil.comparisonModeOptionSingular(rowComparisonMode!)}
      dataUnitName={messageUtil.viewOption(widgetSetting.viewOption!)}
      colorSetting={getColorSetting(widgetSetting.viewOption!)}
      rangeMin={rangeMin}
      rangeMax={rangeMax}
      onClick={onClick}
      enableTooltip={enableTooltip}
    />
  )
}

export default HeatmapDisplay
